import { useEffect, useState } from 'react'
import { ChevronRight, Table2 } from 'lucide-react'
import { schemaApi } from '@/api/services'
import { Spinner } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import type { TableSchema, ColumnSchema } from '@/types'

interface Props {
  onInsertColumn: (table: string, column: string) => void
}

const DRAG_TYPE = 'text/x-sql-column'

const TYPE_COLOR: Record<string, string> = {
  integer: 'text-accent', 'character varying': 'text-green', boolean: 'text-yellow',
  date: 'text-accent2', numeric: 'text-orange-400', text: 'text-green',
  timestamptz: 'text-accent2', time: 'text-accent2',
}

export function SchemaExplorer({ onInsertColumn }: Props) {
  const [tables, setTables] = useState<TableSchema[]>([])
  const [columns, setColumns] = useState<Record<string, ColumnSchema[]>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [loadingTable, setLoadingTable] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    schemaApi.getTables().then(setTables).finally(() => setLoading(false))
  }, [])

  async function toggleTable(name: string) {
    const next = !expanded[name]
    setExpanded(p => ({ ...p, [name]: next }))
    if (next && !columns[name]) {
      setLoadingTable(name)
      const cols = await schemaApi.getColumns(name)
      setColumns(p => ({ ...p, [name]: cols }))
      setLoadingTable(null)
    }
  }

  const filtered = tables.filter(t =>
    !search || t.tableName.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-3 py-2 flex-shrink-0">
        <p className="text-[10px] font-bold tracking-widest uppercase text-muted mb-2">⬡ Schema</p>
        <input
          className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-[11px] font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none"
          placeholder="Search tables…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : filtered.map(t => (
          <div key={t.tableName}>
            <button
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-border/50 transition-colors"
              onClick={() => toggleTable(t.tableName)}
            >
              <ChevronRight className={cn('h-3 w-3 text-muted flex-shrink-0 transition-transform', expanded[t.tableName] && 'rotate-90')} />
              <Table2 className="h-3 w-3 text-accent2 flex-shrink-0" />
              <span className="text-[11px] font-mono text-ink flex-1">{t.tableName}</span>
            </button>

            {expanded[t.tableName] && (
              loadingTable === t.tableName ? (
                <div className="flex justify-center py-2"><Spinner className="h-3 w-3" /></div>
              ) : (
                (columns[t.tableName] ?? []).map(col => (
                  <div
                    key={col.name}
                    draggable
                    onDragStart={e => {
                      e.dataTransfer.setData(DRAG_TYPE, `${t.tableName}."${col.name}"`)
                      e.dataTransfer.effectAllowed = 'copy'
                    }}
                    onClick={() => onInsertColumn(t.tableName, col.name)}
                    className="flex w-full items-center gap-2 pl-9 pr-3 py-1 hover:bg-border/50 transition-colors group cursor-grab active:cursor-grabbing select-none"
                    title="Click or drag — insert column reference"
                  >
                    <span className="text-[10.5px] font-mono text-muted group-hover:text-ink flex-1 text-left transition-colors">{col.name}</span>
                    <span className={cn('text-[9px] font-mono', TYPE_COLOR[col.dataType] ?? 'text-muted')}>
                      {col.dataType.replace('character varying', 'varchar')}
                    </span>
                    {col.isNullable && <span className="text-[8px] text-border2">?</span>}
                  </div>
                ))
              )
            )}
          </div>
        ))}
      </div>
    </div>
  )
}